import { ReactNode, useEffect, useMemo, useState } from 'react';

export interface OptionSelectorOption<T extends string | number> {
  label: ReactNode;
  value: T;
  description?: ReactNode;
}

interface OptionSelectorProps<T extends string | number> {
  title: ReactNode;
  value: T;
  options: OptionSelectorOption<T>[];
  onSelect: (value: T) => void;
  layout?: 'row' | 'column';
  helperText?: ReactNode;
  pageSize?: number;
}

const getDefaultPageSize = (layout: 'row' | 'column') => (layout === 'row' ? 6 : 8);

const OptionSelector = <T extends string | number>({
  title,
  value,
  options,
  onSelect,
  layout = 'row',
  helperText,
  pageSize,
}: OptionSelectorProps<T>) => {
  const resolvedPageSize = pageSize ?? getDefaultPageSize(layout);
  const [page, setPage] = useState(0);

  const totalPages = useMemo(() => {
    if (!options.length) return 1;
    return Math.max(1, Math.ceil(options.length / resolvedPageSize));
  }, [options.length, resolvedPageSize]);

  useEffect(() => {
    if (page > totalPages - 1) {
      setPage(totalPages - 1);
    }
  }, [page, totalPages]);

  useEffect(() => {
    const index = options.findIndex((option) => option.value === value);
    if (index >= 0) {
      const optionPage = Math.floor(index / resolvedPageSize);
      if (optionPage !== page) {
        setPage(optionPage);
      }
    }
  }, [options, page, resolvedPageSize, value]);

  const visibleOptions = useMemo(() => {
    const start = page * resolvedPageSize;
    return options.slice(start, start + resolvedPageSize);
  }, [options, page, resolvedPageSize]);

  const renderOption = (option: OptionSelectorOption<T>) => (
    <button
      key={String(option.value)}
      type="button"
      className={`tv-button${value === option.value ? ' active' : ''}`}
      onClick={() => onSelect(option.value)}
      onKeyDown={(e) => {
        if (e.keyCode === 142 || e.keyCode === 195) {
          e.preventDefault();
          onSelect(option.value)
        }
      }}
      tabIndex={0}
    >
      <span>{option.label}</span>
      {option.description && (
        <span className="helper-text" style={{ display: 'block', marginTop: '0.2rem' }}>
          {option.description}
        </span>
      )}
    </button>
  );

  return (
    <div className="option-block">
      <div>
        <div className="panel-title">{title}</div>
        {helperText && <div className="panel-subtitle">{helperText}</div>}
      </div>
      {visibleOptions.length > 0 ? (
        <div className="option-grid" data-layout={layout}>
          {visibleOptions.map(renderOption)}
        </div>
      ) : (
        <div className="panel-subtitle">No options available.</div>
      )}
      {totalPages > 1 && (
        <div className="inline-actions" style={{ justifyContent: 'space-between' }}>
          <button
            type="button"
            className="tv-button"
            onClick={() => setPage((prev) => (prev - 1 + totalPages) % totalPages)}
          >
            Previous
          </button>
          <span className="helper-text">Page {page + 1} of {totalPages}</span>
          <button
            type="button"
            className="tv-button"
            onClick={() => setPage((prev) => (prev + 1) % totalPages)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default OptionSelector;
